import { useState } from "react";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import {
  BT_NEWSLETTERS,
  NEWSLETTER_TYPE,
} from "@components/Newsletter/types/Newsletter";
import useSendDataToGTM from "@features/analytics/hooks/useSendDataToGTM";
import BTGSuccessMessage from "@features/newsletter/components/BTGSuccessMessage";
import NewsletterForm from "@features/newsletter/components/NewsletterForm";
import { Transition } from "@headlessui/react";
import { cn } from "@util/helpers";

type ArticleBTGNewsletterProps = {
  /** This is the classname of the component. */
  className?: string;
};

/**
 * BTG Newsletter component in article page.
 * https://www.figma.com/design/LHoF60AgvlnXcwv25cns5j/ARTICLE-PAGE--WEB---APP-?node-id=7308-65350&m=dev
 */
const ArticleBTGNewsletter = ({
  className,
}: ArticleBTGNewsletterProps): React.ReactElement => {
  const sendDataToGTM = useSendDataToGTM();
  const [isShowForm, setIsShowForm] = useState(false);

  const newsletter = BT_NEWSLETTERS.find(
    (newsletter) => newsletter.type === NEWSLETTER_TYPE.BTGLOBAL
  );

  if (typeof newsletter === "undefined") return <></>;

  const handleClick = () => {
    setIsShowForm(true);

    sendDataToGTM({
      event: "custom_event",
      eventCategory: "newsletter",
      eventAction: "click",
      eventLabel: "decoding asia 2",
    });
  };

  const transitionClasses = {
    enter: "transition ease-linear duration-200",
    enterFrom: "opacity-0 -translate-y-1",
    enterTo: "opacity-100 translate-y-0",
    leave: "transition ease-in duration-150",
    leaveFrom: "opacity-100 translate-y-0",
    leaveTo: "opacity-0 -translate-y-1",
  };

  return (
    <div
      data-testid="article-btg-newsletter-component"
      data-section-name="newsletter"
      data-section-label="decoding asia 2"
      className={cn("track-impression", className)}
    >
      <Transition show={!isShowForm} as="div" {...transitionClasses}>
        <p className="typo-article-body text-xl font-medium italic leading-150 -tracking-5 text-gray-850">
          {
            "Decoding Asia newsletter: your guide to navigating Asia in a new global order. "
          }

          <span
            className="inline cursor-pointer border-none p-0 font-lucida text-inherit text-xl font-medium italic leading-150 !-tracking-5 text-verticals-btblue !underline outline-none hover:text-blue-500"
            onClick={handleClick}
          >
            Sign up here to get Decoding Asia newsletter.
          </span>

          {" Delivered to your inbox. Free."}
        </p>
      </Transition>

      <Transition
        show={isShowForm}
        as="div"
        className="mx-auto w-full lg:w-2/3"
        {...transitionClasses}
      >
        <NewsletterForm
          className="group w-full transition duration-300 ease-in data-[closed]:opacity-0 lg:w-auto lg:min-w-[360px]"
          buttonClassName={cn(
            "px-2 py-1 flex items-center justify-center text-verticals-btblue hover:opacity-75 border border-blue-50 w-auto"
          )}
          formClassName={cn("py-2 border-b border-gray-350")}
          inputClassName={cn(
            "rounded-full px-4 font-poppins text-base placeholder:text-gray-550"
          )}
          newsletters={[newsletter]}
          isBordered={false}
          placeholder="Your email address"
          signUpButtonIcon={
            <IconFactory
              type={IconTypeEnum.rightArrow}
              className="text-verticals-btblue"
            />
          }
          successMessage={<BTGSuccessMessage className="text-xl" />}
          onSubmitCallback={() => {}} // Deliberately empty to avoid sending GA event
          onSuccessCallback={() => {
            sendDataToGTM({
              event: "custom_event",
              eventCategory: "newsletter",
              eventAction: "signup",
              eventLabel: "decoding asia 2",
            });
          }}
        />
      </Transition>
    </div>
  );
};

export default ArticleBTGNewsletter;
